import { ChatBubbleLeftIcon, CloudArrowUpIcon, PaperClipIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import { type BaseFile, userHasAccountType } from 'config'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { addTaskComment, editTaskComment, getTaskComments, openS3Document, uploadFiles } from 'services'
import { svgLoading } from 'stories/assets'
import { Button, Checkbox } from 'stories/components'
import { changeObject2Array, formatTime } from 'utils'
import { RenderInput } from 'utils/RenderInput'

interface ITaskComment {
  id: string
  comment: string
  createdBy: string
  createdByName: string
  createdAt: number
  files?: Array<{ name: string; fileKey: string }>
  editedAt?: number
  isEdited?: boolean
  isInternal?: boolean
}

interface TaskCommentsProps {
  taskId: number
  filePath?: string
}

const isFileDrag = (event: React.DragEvent) => Array.from(event.dataTransfer?.types || []).includes('Files')

export const TaskComments = ({ taskId, filePath }: TaskCommentsProps) => {
  const [comments, setComments] = useState<ITaskComment[]>([])
  const [newComment, setNewComment] = useState('')
  const [pendingFiles, setPendingFiles] = useState<BaseFile[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isInternal, setIsInternal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragDepth = useRef(0)
  const uploadPath = filePath || `task/comment/${taskId}`
  const auth = useSelector((state: any) => state.auth)
  const rulesConfig = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.RulesConfig] || {})
  const currentUserEmail = auth?.profile?.email

  const canMarkInternal = useMemo(() => {
    const accountTypes = changeObject2Array(rulesConfig.internalAccountTypes || {})
    if (!accountTypes.length || !auth?.profile) return false
    return userHasAccountType(auth.profile, accountTypes)
  }, [auth?.profile, rulesConfig.internalAccountTypes])

  const acceptedFileTypes: string[] = Array.isArray(rulesConfig?.fileExtensions) ? rulesConfig.fileExtensions : []

  const uploadDroppedFiles = async (files: File[]) => {
    const accepted: File[] = []
    const rejected: string[] = []
    files.forEach((file) => {
      const ext = (file.name.toLowerCase().split('.').pop() || '').trim()
      if (!acceptedFileTypes.length || acceptedFileTypes.includes(ext)) accepted.push(file)
      else rejected.push(file.name)
    })
    if (rejected.length === 1)
      toast(`${rejected[0]} is not ${acceptedFileTypes.join(', ')}`, { type: 'error' })
    else if (rejected.length)
      toast(`${rejected.length} files were skipped. Only ${acceptedFileTypes.join(', ')} are accepted.`, {
        type: 'error',
      })
    if (!accepted.length) return

    setIsUploading(true)
    try {
      const keys: string[] = await uploadFiles({ path: uploadPath }, accepted)
      const createdAt = Date.now()
      const uploaded: BaseFile[] = keys.map((key, index) => ({
        id: createdAt + index,
        name: accepted[index].name,
        fileKey: key,
        createdAt,
      }))
      setPendingFiles((prev) => [...prev, ...uploaded])
    } catch (error) {
      console.error('Failed to upload comment attachment:', error)
      toast('Failed to upload attachment', { type: 'error' })
    } finally {
      setIsUploading(false)
    }
  }

  const onDragEnter = (event: React.DragEvent) => {
    if (!isFileDrag(event)) return
    event.preventDefault()
    event.stopPropagation()
    dragDepth.current += 1
    setIsDragOver(true)
  }

  const onDragOver = (event: React.DragEvent) => {
    if (!isFileDrag(event)) return
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = 'copy'
  }

  const onDragLeave = (event: React.DragEvent) => {
    if (!isFileDrag(event)) return
    event.preventDefault()
    event.stopPropagation()
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setIsDragOver(false)
  }

  const onDropFiles = (event: React.DragEvent) => {
    if (!isFileDrag(event)) return
    event.preventDefault()
    event.stopPropagation()
    dragDepth.current = 0
    setIsDragOver(false)
    const files = Array.from(event.dataTransfer.files || [])
    if (files.length) uploadDroppedFiles(files)
  }

  const onPickFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (files.length) uploadDroppedFiles(files)
  }

  const removePendingFile = (fileKey: string) => {
    setPendingFiles((prev) => prev.filter((file) => file.fileKey !== fileKey))
  }

  useEffect(() => {
    loadComments()
  }, [taskId])

  const loadComments = async () => {
    setIsLoading(true)
    try {
      const result = await getTaskComments(taskId)
      setComments(result || [])
    } catch (error) {
      console.error('Failed to load comments:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmitComment = async () => {
    const comment = newComment.trim()
    if (!comment && !pendingFiles.length) return
    if (isUploading) return

    setIsSubmitting(true)
    try {
      const result = await addTaskComment(taskId, comment, pendingFiles, canMarkInternal && isInternal)
      setComments([...comments, result])
      setNewComment('')
      setPendingFiles([])
      setIsInternal(false)
    } catch (error) {
      console.error('Failed to add comment:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmitComment()
    }
  }

  const handleStartEdit = (comment: ITaskComment) => {
    setEditingCommentId(comment.id)
    setEditingText(comment.comment)
  }

  const handleCancelEdit = () => {
    setEditingCommentId(null)
    setEditingText('')
  }

  const handleSaveEdit = async (commentId: string) => {
    if (!editingText.trim()) return

    setIsSubmitting(true)
    try {
      const updatedComment = await editTaskComment(taskId, commentId, editingText.trim())
      setComments(comments.map((c) => (c.id === commentId ? updatedComment : c)))
      setEditingCommentId(null)
      setEditingText('')
    } catch (error) {
      console.error('Failed to edit comment:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>, commentId: string) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSaveEdit(commentId)
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  return (
    <div className="border-t pt-4 mt-4">
      <div className="flex items-center gap-2 mb-4">
        <ChatBubbleLeftIcon className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">Comments ({comments.length})</h3>
      </div>

      {/* Comments List */}
      <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <img src={svgLoading} className="w-6 h-6 animate-spin" />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ChatBubbleLeftIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No comments yet.</p>
          </div>
        ) : (
          comments.map((comment) => {
            const isOwnComment = comment.createdBy === currentUserEmail
            const isEditing = editingCommentId === comment.id

            return (
              <div key={comment.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{comment.createdByName}</span>
                      <span className="text-xs text-gray-500">{comment.createdBy}</span>
                      {comment.isInternal && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                          Internal
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{formatTime(comment.createdAt)}</span>
                      {comment.isEdited && <span className="text-xs text-gray-400 italic">(edited)</span>}
                    </div>
                  </div>
                  {isOwnComment && !isEditing && (
                    <button
                      onClick={() => handleStartEdit(comment)}
                      className="text-gray-500 hover:text-blue-600 transition-colors"
                      title="Edit comment"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    <div
                      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        const keyboardEvent = e as unknown as React.KeyboardEvent<HTMLTextAreaElement>
                        handleEditKeyPress(keyboardEvent, comment.id)
                      }}
                      className={isSubmitting ? 'pointer-events-none opacity-50' : ''}
                    >
                      <RenderInput
                        input={{
                          inputType: 'textarea',
                          type: 'text',
                          title: '',
                          value: editingText,
                          required: false,
                          rows: 3,
                        }}
                        Key="editComment"
                        onChange={(_key: string, value: string) => setEditingText(value)}
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 text-sm bg-red-500 text-white rounded-md"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveEdit(comment.id)}
                        disabled={!editingText.trim() || isSubmitting}
                        className="!py-1 !px-3 !text-sm bg-shade-blue text-white rounded-md"
                      >
                        {isSubmitting ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">Press Ctrl+Enter to save, Esc to cancel</p>
                  </div>
                ) : (
                  <>
                    <div className="text-gray-700 whitespace-pre-wrap break-words">{comment.comment}</div>
                    {comment.files && comment.files.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {comment.files.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:underline cursor-pointer"
                            onClick={() => openS3Document(file.fileKey)}
                          >
                            <PaperClipIcon className="w-4 h-4" />
                            <span>{file.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Add Comment Form */}
      <div
        className="border-t pt-4"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDropFiles}
      >
        <div className={`relative rounded-md ${isDragOver ? 'ring-2 ring-shade-blue' : ''}`}>
          <div
            className="flex-1"
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              const keyboardEvent = e as unknown as React.KeyboardEvent<HTMLTextAreaElement>
              handleKeyPress(keyboardEvent)
            }}
          >
            <RenderInput
              input={{
                inputType: 'textarea',
                type: 'text',
                title: '',
                value: newComment,
                required: false,
                placeholder: 'Add a comment... (Ctrl+Enter to submit)',
                rows: 3,
              }}
              Key="newComment"
              onChange={(_key: string, value: string) => setNewComment(value)}
            />
          </div>
          {isDragOver && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-md border-2 border-dashed border-shade-blue bg-white/90 text-shade-blue pointer-events-none">
              <CloudArrowUpIcon className="w-8 h-8" />
              <span className="text-sm">Drop files to attach</span>
            </div>
          )}
        </div>
        {pendingFiles.length > 0 && (
          <div className="mt-2 space-y-1">
            {pendingFiles.map((file) => (
              <div key={file.fileKey} className="flex items-center gap-2 text-sm text-gray-700">
                <PaperClipIcon className="w-4 h-4 shrink-0 text-gray-500" />
                <button
                  type="button"
                  className="flex-1 truncate text-left text-shade-blue hover:underline"
                  onClick={() => openS3Document(file.fileKey)}
                >
                  {file.name}
                </button>
                <button
                  type="button"
                  className="text-gray-400 hover:text-red-500"
                  title="Remove attachment"
                  onClick={() => removePendingFile(file.fileKey)}
                  disabled={isSubmitting}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-1">Press Ctrl+Enter to submit. Drop files here to attach.</p>
        <div className="flex items-center justify-between mt-2 gap-3">
          <div className="flex items-center gap-3">
            {canMarkInternal ? (
              <Checkbox
                id={`task-comment-internal-${taskId}`}
                title="Internal"
                value={isInternal}
                onChange={(checked) => setIsInternal(checked)}
                className="!w-auto"
              />
            ) : null}
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-shade-blue hover:underline disabled:opacity-50"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting || isUploading}
            >
              {isUploading ? (
                <img src={svgLoading} className="w-4 h-4 animate-spin" />
              ) : (
                <PaperClipIcon className="w-4 h-4" />
              )}
              {isUploading ? 'Uploading...' : 'Attach'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept={acceptedFileTypes.length ? acceptedFileTypes.map((ext) => `.${ext}`).join(',') : undefined}
              onChange={onPickFiles}
            />
          </div>
          <Button
            onClick={handleSubmitComment}
            disabled={(!newComment.trim() && !pendingFiles.length) || isSubmitting || isUploading}
            className="!py-2 !px-4"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <img src={svgLoading} className="w-4 h-4 animate-spin" />
                Adding...
              </span>
            ) : (
              'Add Comment'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
